import { supabase } from '../lib/supabase';

export const workflowService = {
  // Workflow Templates
  async getWorkflowTemplates() {
    const { data, error } = await supabase
      ?.from('workflow_templates')
      ?.select('*')
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    
    return data?.map(template => ({
      id: template?.id,
      name: template?.name,
      description: template?.description,
      triggerType: template?.trigger_type,
      triggerConditions: template?.trigger_conditions,
      actions: template?.actions,
      isActive: template?.is_active,
      createdBy: template?.created_by,
      createdAt: template?.created_at,
      updatedAt: template?.updated_at
    })) || [];
  },

  async createWorkflowTemplate(templateData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('workflow_templates')
      ?.insert({
        name: templateData?.name,
        description: templateData?.description,
        trigger_type: templateData?.triggerType,
        trigger_conditions: templateData?.triggerConditions || {},
        actions: templateData?.actions,
        is_active: templateData?.isActive !== undefined ? templateData?.isActive : true,
        created_by: user?.id
      })
      ?.select()
      ?.single();

    if (error) throw error;
    
    return {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      triggerType: data?.trigger_type,
      triggerConditions: data?.trigger_conditions,
      actions: data?.actions,
      isActive: data?.is_active,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateWorkflowTemplate(templateId, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbUpdates = {};
    if (updates?.name !== undefined) dbUpdates.name = updates?.name;
    if (updates?.description !== undefined) dbUpdates.description = updates?.description;
    if (updates?.triggerType !== undefined) dbUpdates.trigger_type = updates?.triggerType;
    if (updates?.triggerConditions !== undefined) dbUpdates.trigger_conditions = updates?.triggerConditions;
    if (updates?.actions !== undefined) dbUpdates.actions = updates?.actions;
    if (updates?.isActive !== undefined) dbUpdates.is_active = updates?.isActive;

    const { data, error } = await supabase
      ?.from('workflow_templates')
      ?.update(dbUpdates)
      ?.eq('id', templateId)
      ?.select()
      ?.single();

    if (error) throw error;
    
    return {
      id: data?.id,
      name: data?.name,
      description: data?.description,
      triggerType: data?.trigger_type,
      triggerConditions: data?.trigger_conditions,
      actions: data?.actions,
      isActive: data?.is_active,
      createdBy: data?.created_by,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async deleteWorkflowTemplate(templateId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      ?.from('workflow_templates')
      ?.delete()
      ?.eq('id', templateId);

    if (error) throw error;
  },

  // Workflow Executions
  async getWorkflowExecutions(bookingId) {
    const { data, error } = await supabase
      ?.from('workflow_executions')
      ?.select('*, workflow_templates(name)')
      ?.eq('booking_id', bookingId)
      ?.order('started_at', { ascending: false });

    if (error) throw error;
    
    return data?.map(execution => ({
      id: execution?.id,
      templateId: execution?.template_id,
      templateName: execution?.workflow_templates?.name,
      bookingId: execution?.booking_id,
      status: execution?.status,
      startedAt: execution?.started_at,
      completedAt: execution?.completed_at,
      executionLog: execution?.execution_log,
      createdAt: execution?.created_at
    })) || [];
  },

  // Lead Scores
  async getLeadScore(bookingId) {
    const { data, error } = await supabase
      ?.from('lead_scores')
      ?.select('*')
      ?.eq('booking_id', bookingId)
      ?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    
    if (!data) return null;
    
    return {
      id: data?.id,
      bookingId: data?.booking_id,
      engagementScore: data?.engagement_score,
      companySizeScore: data?.company_size_score,
      serviceFitScore: data?.service_fit_score,
      budgetScore: data?.budget_score,
      timelineScore: data?.timeline_score,
      totalScore: data?.total_score,
      scoreFactors: data?.score_factors,
      lastCalculatedAt: data?.last_calculated_at,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateLeadScore(bookingId, scoreData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbUpdates = {};
    if (scoreData?.engagementScore !== undefined) dbUpdates.engagement_score = scoreData?.engagementScore;
    if (scoreData?.companySizeScore !== undefined) dbUpdates.company_size_score = scoreData?.companySizeScore;
    if (scoreData?.serviceFitScore !== undefined) dbUpdates.service_fit_score = scoreData?.serviceFitScore;
    if (scoreData?.budgetScore !== undefined) dbUpdates.budget_score = scoreData?.budgetScore;
    if (scoreData?.timelineScore !== undefined) dbUpdates.timeline_score = scoreData?.timelineScore;
    if (scoreData?.scoreFactors !== undefined) dbUpdates.score_factors = scoreData?.scoreFactors;
    dbUpdates.last_calculated_at = new Date()?.toISOString();

    const { data, error } = await supabase
      ?.from('lead_scores')
      ?.update(dbUpdates)
      ?.eq('booking_id', bookingId)
      ?.select()
      ?.single();

    if (error) throw error;
    
    return {
      id: data?.id,
      bookingId: data?.booking_id,
      engagementScore: data?.engagement_score,
      companySizeScore: data?.company_size_score,
      serviceFitScore: data?.service_fit_score,
      budgetScore: data?.budget_score,
      timelineScore: data?.timeline_score,
      totalScore: data?.total_score,
      scoreFactors: data?.score_factors,
      lastCalculatedAt: data?.last_calculated_at,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Sales Pipeline
  async getSalesPipeline(bookingId) {
    const { data, error } = await supabase
      ?.from('sales_pipeline')
      ?.select('*')
      ?.eq('booking_id', bookingId)
      ?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    
    if (!data) return null;
    
    return {
      id: data?.id,
      bookingId: data?.booking_id,
      currentStage: data?.current_stage,
      stageHistory: data?.stage_history,
      estimatedValue: data?.estimated_value,
      probability: data?.probability,
      expectedCloseDate: data?.expected_close_date,
      notes: data?.notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateSalesPipeline(bookingId, pipelineData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbUpdates = {};
    if (pipelineData?.currentStage !== undefined) {
      dbUpdates.current_stage = pipelineData?.currentStage;
      
      const { data: existing } = await supabase
        ?.from('sales_pipeline')
        ?.select('stage_history')
        ?.eq('booking_id', bookingId)
        ?.single();
      
      const history = existing?.stage_history || [];
      history?.push({
        stage: pipelineData?.currentStage,
        entered_at: new Date()?.toISOString()
      });
      dbUpdates.stage_history = history;
    }
    if (pipelineData?.estimatedValue !== undefined) dbUpdates.estimated_value = pipelineData?.estimatedValue;
    if (pipelineData?.probability !== undefined) dbUpdates.probability = pipelineData?.probability;
    if (pipelineData?.expectedCloseDate !== undefined) dbUpdates.expected_close_date = pipelineData?.expectedCloseDate;
    if (pipelineData?.notes !== undefined) dbUpdates.notes = pipelineData?.notes;

    const { data, error } = await supabase
      ?.from('sales_pipeline')
      ?.update(dbUpdates)
      ?.eq('booking_id', bookingId)
      ?.select()
      ?.single();

    if (error) throw error;
    
    return {
      id: data?.id,
      bookingId: data?.booking_id,
      currentStage: data?.current_stage,
      stageHistory: data?.stage_history,
      estimatedValue: data?.estimated_value,
      probability: data?.probability,
      expectedCloseDate: data?.expected_close_date,
      notes: data?.notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Automated Tasks
  async getAutomatedTasks(bookingId) {
    const { data, error } = await supabase
      ?.from('automated_tasks')
      ?.select('*, user_profiles(full_name, email)')
      ?.eq('booking_id', bookingId)
      ?.order('due_date', { ascending: true });

    if (error) throw error;
    
    return data?.map(task => ({
      id: task?.id,
      workflowExecutionId: task?.workflow_execution_id,
      bookingId: task?.booking_id,
      assignedTo: task?.assigned_to,
      assignedToName: task?.user_profiles?.full_name,
      assignedToEmail: task?.user_profiles?.email,
      title: task?.title,
      description: task?.description,
      priority: task?.priority,
      status: task?.status,
      dueDate: task?.due_date,
      completedAt: task?.completed_at,
      taskMetadata: task?.task_metadata,
      createdAt: task?.created_at,
      updatedAt: task?.updated_at
    })) || [];
  },

  async updateAutomatedTask(taskId, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbUpdates = {};
    if (updates?.status !== undefined) {
      dbUpdates.status = updates?.status;
      if (updates?.status === 'completed') {
        dbUpdates.completed_at = new Date()?.toISOString();
      }
    }
    if (updates?.priority !== undefined) dbUpdates.priority = updates?.priority;
    if (updates?.dueDate !== undefined) dbUpdates.due_date = updates?.dueDate;
    if (updates?.assignedTo !== undefined) dbUpdates.assigned_to = updates?.assignedTo;

    const { data, error } = await supabase
      ?.from('automated_tasks')
      ?.update(dbUpdates)
      ?.eq('id', taskId)
      ?.select('*, user_profiles(full_name, email)')
      ?.single();

    if (error) throw error;
    
    return {
      id: data?.id,
      workflowExecutionId: data?.workflow_execution_id,
      bookingId: data?.booking_id,
      assignedTo: data?.assigned_to,
      assignedToName: data?.user_profiles?.full_name,
      assignedToEmail: data?.user_profiles?.email,
      title: data?.title,
      description: data?.description,
      priority: data?.priority,
      status: data?.status,
      dueDate: data?.due_date,
      completedAt: data?.completed_at,
      taskMetadata: data?.task_metadata,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async getMyTasks() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('automated_tasks')
      ?.select('*, consultation_bookings(client_name, service_interest)')
      ?.eq('assigned_to', user?.id)
      ?.order('due_date', { ascending: true });

    if (error) throw error;
    
    return data?.map(task => ({
      id: task?.id,
      workflowExecutionId: task?.workflow_execution_id,
      bookingId: task?.booking_id,
      clientName: task?.consultation_bookings?.client_name,
      serviceInterest: task?.consultation_bookings?.service_interest,
      assignedTo: task?.assigned_to,
      title: task?.title,
      description: task?.description,
      priority: task?.priority,
      status: task?.status,
      dueDate: task?.due_date,
      completedAt: task?.completed_at,
      taskMetadata: task?.task_metadata,
      createdAt: task?.created_at,
      updatedAt: task?.updated_at
    })) || [];
  }
};

export default workflowService;
